import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Typography, TextField, Button, Grid, Card, CardContent, Alert } from '@mui/material';
import Spinner from '../../components/Spinner';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import api from '../../api';

const WritingStudio: React.FC = () => {
  const navigate = useNavigate();
  const [guides, setGuides] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGuides = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let queryStr = `?type=writing`;
      if (search.trim()) {
        queryStr += `&search=${encodeURIComponent(search.trim())}`;
      }

      const res = await api.get(`/language-hub${queryStr}`);
      if (res.data && res.data.success) {
        setGuides(res.data.data);
      } else {
        setError('Failed to fetch writing guides.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error connecting to writing studio API');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchGuides();
  }, [fetchGuides]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchGuides();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Back button */}
      <Button 
        startIcon={<ArrowBackIcon />} 
        onClick={() => navigate('/language-hub')} 
        sx={{ mb: 4 }}
      >
        Language Hub
      </Button>

      {/* Header */}
      <Box sx={{ mb: 5 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, mb: 1.5 }}>
          Writing Studio
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Craft powerful prose. Access essay structures, standard formatting templates, and style manuals.
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 5 }}>
        <form onSubmit={handleSearchSubmit}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search writing guides (e.g. Essay, Letter template)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
            />
            <Button type="submit" variant="contained" size="large" sx={{ px: 4 }}>
              Search
            </Button>
          </Box>
        </form>
      </Box>

      {/* Error display */}
      {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

      {/* Writing guides */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <Spinner />
        </Box>
      ) : guides.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8, border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">No writing guides or templates found matching your query.</Typography>
        </Box>
      ) : (
        <Grid container spacing={4}>
          {guides.map((item) => (
            <Grid item xs={12} key={item._id}>
              <Card 
                sx={{ 
                  borderRadius: 4, 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  background: 'rgba(255, 255, 255, 0.01)',
                  p: 2
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" component="h2" sx={{ fontWeight: 800, color: '#3b82f6' }}>
                      {item.title}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      color="primary" 
                      size="small" 
                      sx={{ borderRadius: 4, pointerEvents: 'none' }}
                    >
                      {item.category || 'General'}
                    </Button>
                  </Box>

                  <Box 
                    sx={{ 
                      color: 'text.primary',
                      '& p': { mb: 2, lineHeight: 1.8 },
                      '& h3': { fontSize: '1.25rem', fontWeight: 700, mt: 3, mb: 1.5, color: '#3b82f6' },
                      '& ul': { pl: 3, mb: 2 },
                      '& li': { mb: 1, lineHeight: 1.7 },
                      '& pre': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.2)', 
                        p: 3, 
                        borderRadius: 2, 
                        border: '1px solid rgba(255,255,255,0.05)',
                        overflowX: 'auto',
                        fontFamily: 'monospace',
                        lineHeight: 1.6
                      }
                    }}
                  >
                    <ReactMarkdown>{item.content}</ReactMarkdown>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default WritingStudio;
